import { ChevronRightIcon, MapPinIcon } from '@heroicons/react/24/outline';

import { pendingPreparations } from '../../../domain/menage/preparations';
import type { HousekeepingRoomStatus } from '../../../pages/housekeeping/types/housekeeping';
import Text from '../../components/Text';
import { emplacementChambre, libelleStatut, libelleType } from '../../helpers/menage';
import { t } from '../../i18n/langue';

/**
 * Une chambre de la liste — lot 3 Odomo.
 *
 * Tapotable seulement quand `onOuvrir` est fourni : la liste de demain est en
 * lecture seule, et une carte qui a l'air d'un contrôle sans en être un promet un
 * geste qui n'aboutit pas. Sans `onOuvrir`, la carte est un simple bloc.
 */
interface CarteChambreProps {
    chambre: HousekeepingRoomStatus;
    onOuvrir?: () => void;
}

function Contenu({ chambre }: { chambre: HousekeepingRoomStatus }) {
    const emplacement = emplacementChambre(chambre);
    const type = libelleType(chambre.cleaning_type);
    const preparations = pendingPreparations(chambre).length;
    // Espace commun (réception, salle de séminaire…) : pas une chambre, le type « spécial » n'apprend rien.
    const details = chambre.is_common_area
        ? [t('menage.carte.espaceCommun'), emplacement, libelleStatut(chambre.status)].filter(Boolean).join(' · ')
        : [emplacement, type, libelleStatut(chambre.status)].filter(Boolean).join(' · ');

    return (
        <span className="min-w-0 flex-1">
            <Text variante="sousTitre" as="span" className="flex items-center gap-1.5 truncate">
                {chambre.is_common_area && <MapPinIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-main-color" />}
                <span className="truncate">{chambre.room_name}</span>
            </Text>
            <Text variante="petit" ton="attenue" as="span" className="block truncate">
                {details}
            </Text>
            {(preparations > 0 || chambre.is_dnd) && (
                <span className="mt-1 flex flex-wrap gap-1.5">
                    {preparations > 0 && (
                        <span className="rounded-full bg-main-color-light px-2 py-0.5">
                            <Text variante="petit" ton="marque" as="span">
                                {t('menage.carte.aPreparer', { n: preparations })}
                            </Text>
                        </span>
                    )}
                    {chambre.is_dnd && (
                        <span className="rounded-full border border-alert-text-color px-2 py-0.5">
                            <Text variante="petit" ton="danger" as="span">
                                {t('menage.carte.nePasDeranger')}
                            </Text>
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}

export default function CarteChambre({ chambre, onOuvrir }: CarteChambreProps) {
    if (!onOuvrir) {
        return (
            <div className="flex min-h-touch items-center gap-3 rounded-xl bg-white px-4 py-3">
                <Contenu chambre={chambre} />
            </div>
        );
    }

    return (
        // mobile-check-ignore — la carte EST le contrôle, comme la ligne de
        // conversation : un <div onClick> serait inaccessible au clavier.
        <button
            type="button"
            onClick={onOuvrir}
            className="flex min-h-touch w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left"
        >
            <Contenu chambre={chambre} />
            <ChevronRightIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-text-color-light" />
        </button>
    );
}
